import React, { useRef, useState } from "react";
import { Camera, X, Lock, Paperclip } from "lucide-react";
import { compressEventAttachmentFile } from "@/lib/eventAttachment";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";

// Event attachment picker. Mirrors AvatarImagePicker but tuned for
// landscape rectangles (a school flyer, a concert ticket) and a
// single attachment per event.
//
// Props:
//   value          current data URL or "" / null
//   onChange(v)    called with new data URL or "" to clear
//   tier           "free" | "plus" | "grand" | "extended"
//   onUpgradeClick optional handler — opens BillingSheet on tap
export default function EventAttachmentPicker({
  value,
  onChange,
  tier = "free",
  onUpgradeClick,
}) {
  const { t } = useI18n();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const paid = tier !== "free";

  const openPicker = () => {
    if (!paid) {
      if (onUpgradeClick) onUpgradeClick();
      else toast.info("Photo attachments need the Plus plan or above.");
      return;
    }
    fileRef.current?.click();
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!f) return;
    setBusy(true);
    try {
      const dataUrl = await compressEventAttachmentFile(f);
      onChange(dataUrl);
    } catch (err) {
      toast.error(err?.message || "Couldn't read that image");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      {value ? (
        <div className="relative ff-card p-2 inline-block max-w-full">
          <img
            src={value}
            alt="Attached"
            className="block max-w-full max-h-56 rounded-lg object-contain"
            data-testid="event-attachment-preview"
          />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-[color:var(--ink)] text-[color:var(--bg)] flex items-center justify-center shadow"
            data-testid="event-attachment-clear"
            aria-label="Remove photo"
          >
            <X className="w-3.5 h-3.5" strokeWidth={2.2} />
          </button>
        </div>
      ) : null}

      <button
        type="button"
        onClick={openPicker}
        disabled={busy}
        className={`ff-chip ff-chip-sm inline-flex items-center gap-1.5 ${
          !paid ? "opacity-70" : ""
        }`}
        data-testid="event-attachment-pick"
      >
        {!paid ? (
          <Lock className="w-3.5 h-3.5" strokeWidth={1.75} />
        ) : value ? (
          <Camera className="w-3.5 h-3.5" strokeWidth={1.75} />
        ) : (
          <Paperclip className="w-3.5 h-3.5" strokeWidth={1.75} />
        )}
        {busy
          ? t({ en: "Compressing…", da: "Komprimerer…" })
          : value
          ? t({ en: "Change photo", da: "Skift foto" })
          : t({ en: "Attach photo", da: "Vedhæft foto" })}
        {!paid && (
          <span className="ml-1 text-[10px] tracking-wider uppercase text-[color:var(--ink-3)]">
            Plus
          </span>
        )}
      </button>

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
        onChange={onFile}
        className="hidden"
        data-testid="event-attachment-input"
      />
    </div>
  );
}
